import groupBy from 'lodash/groupBy';
import sumBy from 'lodash/sumBy';

import * as React from 'react';
import { removeSeatFromCart, saveSeatRemoval } from '../../../cart/actions/cart-actions';
import { PriceType } from '../../../classes/price-type';
import { CartSeat, Seat } from '../../../classes/seat';
import { convertStringToStringList } from '../../../helpers/array-helpers';
import { getCurrencyFormat } from '../../../helpers/currency-format';
import { closeSeatmapCart, selectGroupedSeats, selectSeat } from '../../../seatmap/actions/seatmap-actions';
import LineItem from './line-item';

export interface ICartItemsProps {
	cartSeats: Array<CartSeat>;
	dispatch: any /*Dispatch<any> can't interpret react-redux dispatch method and react-redux typings exports Redux.Dispatch.*/;
	itemId: number;
	seatMapId: string;
	selectedSeatIds: string;
	allSeats: Array<Seat>;
	selectedPriceTypes: Array<PriceType>;

	cartSubtotalLabel: string;
	cartEmptyNotice: string;
	useZoneDescription: boolean;
	groupSeatCartItemText: string;
	currencyFormat: string;
	exchangeFreeLabel: string;
	brandId: string;
	hidePricesInCart: boolean;
	fetchSeatViewImage: boolean;
	displaySeatFee?: boolean;
	priceFeeHeading?: string;
	feeAmountText?: string;
	ticketCostText?: string;
}

// List of cart items (LineItem components) and subtotal
class CartItems extends React.Component<ICartItemsProps, { seatToRemove: CartSeat }> {

	shouldComponentUpdate(nextProps: ICartItemsProps) {
		return this.props.seatMapId != nextProps.seatMapId
			|| this.props.selectedSeatIds != nextProps.selectedSeatIds
			|| this.props.allSeats.length != nextProps.allSeats.length
			|| this.props.selectedPriceTypes.length != nextProps.selectedPriceTypes.length
			|| this.props.cartSeats.length != nextProps.cartSeats.length;
	}

	render() {
		const { cartSeats } = this.props;

		return (

			<ul className="cart-container-inner">
				{cartSeats.length > 0 ?
					this.getLineItems(cartSeats)

					: <li className="cart-item"><p className="caption-paragraph">{this.props.cartEmptyNotice}</p></li>}
			</ul>

		);
	}

	getIsSelected(seat: CartSeat) {
		return convertStringToStringList(this.props.selectedSeatIds).some(selectedSeatId => selectedSeatId === seat.seatId);
	}

	// Takes in array of seats and outputs an array of LineItem components: first single seats, then group seats
	getLineItems(seats: Array<CartSeat>): Array<JSX.Element> {
		const seatsByGrouped = groupBy(seats, (seat) => seat.isGroupedSeat ? 'group' : 'single');
		const singleSeats = seatsByGrouped['single'] || [];
		const groupSeats = seatsByGrouped['group'] || [];

		const singleLineItems = this.getSingleLineItems(singleSeats);
		const groupLineItems = this.getGroupLineItems(groupSeats);
		return singleLineItems.concat(groupLineItems);
	}

	// Takes in array of single seats and ouputs an array of LineItem components
	getSingleLineItems(seats: Array<CartSeat>): Array<JSX.Element> {
		return seats.map(seat =>
			<LineItem key={seat.tessituraSeat.seatNumber.toString()}
				description={this.getSingleSeatDescription(seat)}
				isSelected={this.getIsSelected(seat)}
				onSelect={this.viewFromSeatIsActive(seat) ? () => this._onCartItemClick(seat) : null}
				onRemove={() => this._onRemoveCartItemClick(seat)}
				priceTypeDescription={seat.selectedPricing.priceTypeDescription}
				price={this.cartItemPriceText(seat.selectedPricing.priceCarted + seat.selectedPricing.feeAmount)}
				priceFeeHeading={this.props.priceFeeHeading}
				fee={this.cartItemFeeText(seat.selectedPricing.feeAmount)}
				baseTicketCost={this.cartItemBasePriceText(seat.selectedPricing.price)}
				displaySeatFee={this.props.displaySeatFee}
			/>
		);
	}

	// LineItem description for a single seat
	getSingleSeatDescription(seat: CartSeat) {
		return `${this.getZoneOrSection(seat)} ${seat.tessituraSeat.rowText} - ${seat.tessituraSeat.numberText}`;
	}

	// Zone text or section text, depending on app setting
	getZoneOrSection(seat: CartSeat) {
		var useZoneDescription = (this.props.useZoneDescription && seat.zoneDescription !== null);
		return useZoneDescription ? seat.zoneDescription : seat.tessituraSeat.sectionDescription;
	}

	// Takes in array of group seats, groups by svgPathId, outputs array of LineItem components
	getGroupLineItems(seats: Array<CartSeat>): Array<JSX.Element> {
		let lineItems: Array<JSX.Element> = [];
		const groupSeatsByPath = groupBy(seats, (seat) => seat.svgPathId);

		for (var pathId in groupSeatsByPath) {
			const groupedSeats = groupSeatsByPath[pathId];
			const section = groupedSeats[0].tessituraSeat.sectionDescription;
			const totalPriceText = this.props.hidePricesInCart ? "" : this.cartItemPriceText(sumBy(groupedSeats, (seat) => seat.selectedPricing.priceCarted + seat.selectedPricing.feeAmount));
			const totalFee = this.cartItemFeeText(sumBy(groupedSeats, (seat) => seat.selectedPricing.feeAmount));
			const totalBasePrice = this.cartItemPriceText(sumBy(groupedSeats, (seat) => seat.selectedPricing.price));
			const totalFeeText = this.props.displaySeatFee && totalFee ? `<span>${this.props.feeAmountText}</span> <span>${totalFee}</span>` : "";
			const totalTicketCostText = this.props.displaySeatFee && totalFee ? `<span>${this.props.ticketCostText}</span> <span>${totalBasePrice}</span>` : "";

			const priceTypeDescription = groupedSeats[0].selectedPricing.priceTypeDescription;

			const allCartSeatsInSamePath = this.props.allSeats.filter(allSeat => allSeat.svgPathId === pathId);
			const onCartItemsClick = () => this._onCartItemsClick(allCartSeatsInSamePath.map(allSeat => allSeat as CartSeat));

			lineItems.push(
				<LineItem key={`groupseat-${pathId}`}
					description={this.getGroupLineItemDescription(groupedSeats.length, section)}
					isSelected={this.getIsSelected(groupedSeats[0])}
					onSelect={onCartItemsClick}
					onRemove={() => { this._onRemoveCartItemsClick(groupedSeats) }}
					price={totalPriceText}
					priceTypeDescription={priceTypeDescription}
					priceFeeHeading={this.props.priceFeeHeading}
					fee={totalFeeText}
					baseTicketCost={totalTicketCostText}
					displaySeatFee={this.props.displaySeatFee}
				/>
			);
		}

		return lineItems;
	}

	getGroupLineItemDescription(number: number, section: string) {
		const groupSeatText = this.props.groupSeatCartItemText;
		return groupSeatText.replace("{0}", number.toString()).replace("{1}", section);
	}

    viewFromSeatIsActive(seat: CartSeat): boolean {
        // This will only return whether view from seat is globally available, or the current level contains this seat. It does not test whether the seat has an image available.
		if (this.props.fetchSeatViewImage) {
			const seatIsInCurrentSeatmap = this.props.allSeats.filter(s => s.seatId == seat.seatId).length > 0;
			const seatHasAssociatedSeatmap = !!seat.associatedSeatmapId;
			return seatIsInCurrentSeatmap || seatHasAssociatedSeatmap;
        }
        return false;
    }

    _onRemoveCartItemClick(cartItem: CartSeat) {
        this.props.dispatch(saveSeatRemoval(cartItem))
        this.props.dispatch(removeSeatFromCart(cartItem, this.props.selectedPriceTypes, this.props.brandId, false));
    }

	_onRemoveCartItemsClick(cartItems: Array<CartSeat>) {
		cartItems.forEach(item =>
			this.props.dispatch(removeSeatFromCart(item, this.props.selectedPriceTypes, this.props.brandId, false))
		);
	}

	_onCartItemClick(cartItem: CartSeat) {
		this.props.dispatch(selectSeat(cartItem, true, cartItem.associatedSeatmapId || this.props.seatMapId, this.props.fetchSeatViewImage));
		this.props.dispatch(closeSeatmapCart(false));
	}

	// Click handler for a group seat LineItem
	_onCartItemsClick(cartItems: Array<CartSeat>) {
		// Assumes view for the first seat in the array
		this.props.dispatch(selectGroupedSeats(cartItems, this.props.seatMapId, this.props.fetchSeatViewImage));
	}

	cartItemPriceText(price: number): string {
		if (this.props.hidePricesInCart) {
			return "";
		}

		if (price > 0 || this.props.exchangeFreeLabel === "") {
			return getCurrencyFormat(this.props.currencyFormat, price);
		}

		return this.props.exchangeFreeLabel;
	}

	cartItemFeeText(price: number): string {
		if (!this.props.displaySeatFee) {
			return "";
		}

		if (price > 0) {
			return `<span>${this.props.feeAmountText}</span> <span>${getCurrencyFormat(this.props.currencyFormat, price)}</span>`;
		}

		return "";

	}

	cartItemBasePriceText(price: number): string {
		if (!this.props.displaySeatFee) {
			return "";
		}

		if (price > 0) {
			return `<span>${this.props.ticketCostText}</span> <span>${getCurrencyFormat(this.props.currencyFormat, price)}</span>`;
		}

		return "";

	}
}

export default CartItems;